import type { CustomCellRendererProps } from "ag-grid-react";
import type { JSX } from "react";
import { IconButton } from "../../iconButton/IconButton";

export interface LockCellParams<TData = unknown> {
  /** Reads the row's locked state, which lives with the caller. */
  isLocked: (data: TData) => boolean;
  /** Receives the grid `context` too, so static column defs can reach page handlers. */
  onToggle: (data: TData, context: unknown) => void;
  /** Hover text, rendered through the kit's Tooltip. */
  tooltip?: (locked: boolean, data: TData) => string;
  /** Overrides the default "Lock row" / "Unlock row" accessible name. */
  ariaLabel?: (locked: boolean, data: TData) => string;
}

/** A lock/unlock toggle for a grid row; the locked state lives with the caller. */
export function LockCell<TData>({
  data,
  context,
  isLocked,
  onToggle,
  tooltip,
  ariaLabel
}: CustomCellRendererProps<TData> & LockCellParams<TData>): JSX.Element | null {
  if (!data) return null;
  const locked = isLocked(data);
  return (
    <IconButton
      iconName={locked ? "lock-filled" : "lock-open"}
      variant="tertiary"
      size="xs"
      onClick={() => onToggle(data, context)}
      tooltip={tooltip?.(locked, data)}
      aria-label={
        ariaLabel?.(locked, data) ?? (locked ? "Unlock row" : "Lock row")
      }
      aria-pressed={locked}
    />
  );
}
